import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ── Types ───────────────────────────────────────────────────────────────

export type Category = "new" | "enhancement" | "fix";
/** ``permission`` is gated on the entry's ``required_permission`` instead of a
 *  flat tier — see PermissionOption below. */
export type Visibility = "all" | "internal" | "admin" | "permission";
export type Status = "draft" | "published";

export interface PermissionOption {
  key: string;
  feature: string;
  action: string;
  description: string;
}

export interface PermissionOptionGroup {
  category: string;
  options: PermissionOption[];
}

export interface EntryImage {
  url: string;
  caption?: string;
}

export interface EntryLink {
  url: string;
  label: string;
}

export interface Entry {
  id: string;
  title: string;
  category: Category;
  description: string;
  project: string;
  visibility: Visibility;
  required_permission: string | null;
  status: Status;
  images: EntryImage[] | null;
  links: EntryLink[] | null;
  build_number: string | null;
  release_date: string; // YYYY-MM-DD
  sort_order: number;
  git_commit_hash: string | null;
  git_repo: string | null;
  auto_generated: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EntryListResponse {
  items: Entry[];
  total: number;
}

export interface EntryCreate {
  title: string;
  category: Category;
  description: string;
  project: string;
  visibility?: Visibility;
  /** Required when ``visibility === "permission"``, rejected otherwise. */
  required_permission?: string | null;
  images?: EntryImage[] | null;
  links?: EntryLink[] | null;
  build_number?: string | null;
  release_date?: string | null;
  sort_order?: number;
}

export type EntryUpdate = Partial<EntryCreate & { status: Status }>;

export interface GitRepoInfo {
  slug: string;
  label: string;
  path: string;
  exists: boolean;
}

export interface GitImportResult {
  repo: string;
  total_commits: number;
  imported: number;
  skipped_duplicate: number;
  skipped_uncategorized: number;
  errors: string[];
}

export interface GitImportStatus {
  running: boolean;
  started_at: string | null;
  finished_at: string | null;
  results: GitImportResult[];
  error: string | null;
}

export interface GitImportForm {
  repos?: string[];
  since?: string | null;
  max_count?: number;
}

export interface GenerateDescriptionForm {
  title: string;
  category: Category;
  project: string;
  build_number?: string | null;
  git_commit_hash?: string | null;
  existing_description?: string | null;
}

// ── User-facing queries ─────────────────────────────────────────────────

const BASE = "/whats-new";

export interface ListEntriesParams {
  category?: Category;
  project?: string;
  skip?: number;
  limit?: number;
}

function listEntriesPath(params: ListEntriesParams): string {
  const search = new URLSearchParams();
  if (params.category) search.set("category", params.category);
  if (params.project) search.set("project", params.project);
  if (params.skip !== undefined) search.set("skip", String(params.skip));
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const qs = search.toString();
  return `${BASE}/${qs ? `?${qs}` : ""}`;
}

export function useEntries(
  params: ListEntriesParams = {},
  options?: Partial<UseQueryOptions<EntryListResponse>>,
) {
  return useQuery({
    queryKey: ["whats_new", "entries", params],
    queryFn: () => apiFetch<EntryListResponse>(listEntriesPath(params)),
    ...options,
  });
}

export function useUnreadCount(
  options?: Partial<UseQueryOptions<{ count: number }>>,
) {
  return useQuery({
    queryKey: ["whats_new", "unread-count"],
    queryFn: () => apiFetch<{ count: number }>(`${BASE}/unread-count`),
    ...options,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>(`${BASE}/mark-read`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["whats_new"] }),
  });
}

export function useDismiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (session_token: string) =>
      apiFetch<void>(`${BASE}/dismiss`, {
        method: "POST",
        body: { session_token },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["whats_new", "unread-count"] }),
  });
}

// ── Admin queries ───────────────────────────────────────────────────────

export interface AdminListParams {
  status?: Status;
  category?: Category;
  project?: string;
  skip?: number;
  limit?: number;
}

function adminListPath(params: AdminListParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.category) search.set("category", params.category);
  if (params.project) search.set("project", params.project);
  if (params.skip !== undefined) search.set("skip", String(params.skip));
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const qs = search.toString();
  return `${BASE}/admin/entries${qs ? `?${qs}` : ""}`;
}

export function useAdminEntries(params: AdminListParams = {}) {
  return useQuery({
    queryKey: ["whats_new", "admin", "entries", params],
    queryFn: () => apiFetch<EntryListResponse>(adminListPath(params)),
  });
}

export function useCreateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EntryCreate) =>
      apiFetch<Entry>(`${BASE}/admin/entries`, { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["whats_new"] }),
  });
}

export function useUpdateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, body }: { entryId: string; body: EntryUpdate }) =>
      apiFetch<Entry>(`${BASE}/admin/entries/${entryId}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["whats_new"] }),
  });
}

export function useDeleteEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      apiFetch<void>(`${BASE}/admin/entries/${entryId}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["whats_new"] }),
  });
}

export function usePublishEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      apiFetch<Entry>(`${BASE}/admin/entries/${entryId}/publish`, {
        method: "POST",
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["whats_new"] }),
  });
}

export function useUnpublishEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      apiFetch<Entry>(`${BASE}/admin/entries/${entryId}/unpublish`, {
        method: "POST",
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["whats_new"] }),
  });
}

// ── Git import ──────────────────────────────────────────────────────────

export function useGitImportRepos() {
  return useQuery({
    queryKey: ["whats_new", "admin", "git-import", "repos"],
    queryFn: () =>
      apiFetch<GitRepoInfo[]>(`${BASE}/admin/git-import/repos`),
  });
}

export function useGitImportStatus(
  options?: Partial<UseQueryOptions<GitImportStatus>>,
) {
  return useQuery({
    queryKey: ["whats_new", "admin", "git-import", "status"],
    queryFn: () =>
      apiFetch<GitImportStatus>(`${BASE}/admin/git-import/status`),
    ...options,
  });
}

export function useTriggerGitImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GitImportForm) =>
      apiFetch<{ status: string }>(`${BASE}/admin/git-import`, {
        method: "POST",
        body,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["whats_new"] }),
  });
}

// ── Image upload + AI ──────────────────────────────────────────────────

export async function uploadImage(file: File): Promise<{ url: string; filename: string }> {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<{ url: string; filename: string }>(`${BASE}/admin/upload-image`, {
    method: "POST",
    body: form,
  });
}

/** Declared permissions grouped by sidebar category — the visibility picker's
 *  options. Static config, so it's cached for the session. */
export function usePermissionOptions(enabled = true) {
  return useQuery({
    queryKey: ["whats_new", "admin", "permission-options"],
    queryFn: () =>
      apiFetch<PermissionOptionGroup[]>(`${BASE}/admin/permission-options`),
    enabled,
    staleTime: Infinity,
  });
}

export function useGenerateDescription() {
  return useMutation({
    mutationFn: (body: GenerateDescriptionForm) =>
      apiFetch<{ description: string }>(`${BASE}/admin/generate-description`, {
        method: "POST",
        body,
      }),
  });
}
